/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import dayjs, { Dayjs } from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

export enum DateRangePreset {
    OneDay = '1d',
    OneWeek = '1w',
    TwoWeeks = '2w',
    OneMonth = '1m',
    Custom = 'custom',
}

export const DATE_RANGE_PRESET_ORDER: DateRangePreset[] = [
    DateRangePreset.OneDay,
    DateRangePreset.OneWeek,
    DateRangePreset.TwoWeeks,
    DateRangePreset.OneMonth,
    DateRangePreset.Custom,
]

/** Inclusive day span for a preset (Custom has no fixed span). */
export const getDateRangePresetDayCount = (preset: DateRangePreset): number | null => {
    switch (preset) {
        case DateRangePreset.OneDay:
            return 1
        case DateRangePreset.OneWeek:
            return 7
        case DateRangePreset.TwoWeeks:
            return 14
        case DateRangePreset.OneMonth:
            return null
        case DateRangePreset.Custom:
            return null
        default:
            return null
    }
}

/**
 * Build an inclusive range from an anchor date for the given preset.
 * Month = same calendar day next month minus 1 day (e.g. 1 Jun → 30 Jun).
 */
export const buildDateRangeFromPreset = (
    preset: DateRangePreset,
    anchor: Dayjs = dayjs()
): { from: Dayjs; to: Dayjs } | null => {
    const start = anchor.startOf('day')

    switch (preset) {
        case DateRangePreset.OneDay:
            return { from: start, to: start }
        case DateRangePreset.OneWeek:
            return { from: start, to: start.add(6, 'day') }
        case DateRangePreset.TwoWeeks:
            return { from: start, to: start.add(13, 'day') }
        case DateRangePreset.OneMonth:
            return { from: start, to: start.add(1, 'month').subtract(1, 'day') }
        case DateRangePreset.Custom:
            return null
        default:
            return null
    }
}

export const resolveDateRangePreset = (from: Dayjs | null, to: Dayjs | null): DateRangePreset => {
    if (!from || !to) {
        return DateRangePreset.Custom
    }

    const start = from.startOf('day')
    const end = to.startOf('day')
    if (end.isBefore(start)) {
        return DateRangePreset.Custom
    }

    const inclusiveDays = end.diff(start, 'day') + 1

    if (inclusiveDays === 1) {
        return DateRangePreset.OneDay
    }
    if (inclusiveDays === 7) {
        return DateRangePreset.OneWeek
    }
    if (inclusiveDays === 14) {
        return DateRangePreset.TwoWeeks
    }

    const monthRange = buildDateRangeFromPreset(DateRangePreset.OneMonth, start)
    if (monthRange && end.isSame(monthRange.to, 'day')) {
        return DateRangePreset.OneMonth
    }

    return DateRangePreset.Custom
}

/** Monday-first calendar cells covering the visible month (incl. adjacent month days). */
export const buildMonthCalendarDays = (viewMonth: Dayjs): Dayjs[] => {
    const firstOfMonth = viewMonth.startOf('month')
    // dayjs().day(): 0=Sun … 6=Sat → Monday-first offset
    const mondayOffset = (firstOfMonth.day() + 6) % 7
    const gridStart = firstOfMonth.subtract(mondayOffset, 'day')

    return Array.from({ length: 42 }, (_, index) => gridStart.add(index, 'day'))
}

export const isDateInInclusiveRange = (
    date: Dayjs,
    from: Dayjs | null,
    to: Dayjs | null
): boolean => {
    if (!from || !to) {
        return false
    }
    const day = date.startOf('day')
    const start = from.startOf('day')
    const end = to.startOf('day')
    return (
        (day.isSame(start, 'day') || day.isAfter(start, 'day')) &&
        (day.isSame(end, 'day') || day.isBefore(end, 'day'))
    )
}

export const formatDateRangeLabel = (from: Dayjs | null, to: Dayjs | null, empty = ''): string => {
    if (!from && !to) {
        return empty
    }
    if (from && !to) {
        return from.format('D MMM YYYY')
    }
    if (!from && to) {
        return to.format('D MMM YYYY')
    }
    if (from && to) {
        if (from.isSame(to, 'day')) {
            return from.format('D MMM YYYY')
        }
        return `${from.format('D MMM YYYY')} - ${to.format('D MMM YYYY')}`
    }
    return empty
}

/** Parse filter dates stored as DD.MM.YYYY (API) or YYYY-MM-DD (legacy UI state). */
export const parseFilterDate = (value?: string | null): Dayjs | null => {
    if (!value) {
        return null
    }
    const trimmed = String(value).trim()
    if (!trimmed) {
        return null
    }

    const dmy = dayjs(trimmed, 'DD.MM.YYYY', true)
    if (dmy.isValid()) {
        return dmy
    }

    const ymd = dayjs(trimmed, 'YYYY-MM-DD', true)
    if (ymd.isValid()) {
        return ymd
    }

    const fallback = dayjs(trimmed)
    return fallback.isValid() ? fallback : null
}

/** Format a day for backend table search range filters. */
export const formatFilterDate = (date: Dayjs): string => date.format('DD.MM.YYYY')
